/**
 * `key=value` connection-string scanners, each reading EXACTLY as that format's own parser does and answering `null`
 * where that parser would reject the text (Option C C3 reviews r2 C3-10, r3 C3-15, r5 C3-21: a reading counts only when
 * it is strictly valid for its format — detection never takes a looser shared rule).
 *
 * - `scanDotNetConnectionString(text, 'sqlclient')` — .NET's `DbConnectionOptions` (SqlClient rules): `;`-separated,
 *   keys may hold spaces (`==` is a literal `=`), `"…"` / `'…'` wrap a value (a doubled mark is the mark); after a closing
 *   mark only whitespace may precede the `;`; an unquoted value runs to the `;`, is trimmed, and may not END with a quote.
 *   An unterminated quote, a key with no `=`, or a control character: rejected.
 * - `scanDotNetConnectionString(text, 'odbc')` — the same parser with ODBC rules: `{…}` wraps a value, quotes are
 *   ordinary characters, and `}}` inside braces is one `}` — the rule of .NET's `OdbcConnectionStringBuilder` and of the
 *   Microsoft JDBC driver's `;`-properties (from 8.4 "closing braces must be escaped"). The SQL Server ODBC driver's own
 *   keyword page says instead that the first `}` ends the value, so a braced ODBC value holding `}}` may read differently
 *   there.
 * - `scanLibpqConninfo(text)` — libpq's `conninfo_parse`: whitespace-separated, spaces around `=` allowed, `'…'` wraps a
 *   value, a backslash escapes the next character inside or outside quotes (a lone final one is dropped), braces and
 *   double quotes are ordinary; the next keyword may follow a closing quote directly. A keyword without `=`, or an
 *   unterminated quote: rejected. (Unknown keywords are the caller's check — `LIBPQ_KEYWORDS`.)
 * - `scanKeyValues(text, PLAIN_SEMICOLON_SYNTAX)` — a plain `;` split, no wrapping (Azure Storage, as its SDK reads it).
 *
 * Pure and process-agnostic. Never log what they return: values carry passwords.
 */

/** A quote or brace character that can open a quoted value in some format. */
export type QuoteMark = '"' | "'" | '{';

/**
 * A pair as a scanner read it. `opening` — the value was NOT read as quoted, yet its text as written begins with a quote or
 * brace character: the format does not treat that character as quoting, so the author meant quoting it will not honour
 * (C3 review r5 ruling: such a string is refused, naming the character). `braced` — the value was read from ODBC `{…}`
 * (so its text as written, braces and all, can be told from a bare one).
 */
export type KeyValuePair = { key: string; value: string; opening?: QuoteMark; braced?: true };

const OPENING = /^["'{]/u;
const openingOf = (text: string): QuoteMark | undefined => (OPENING.test(text) ? (text[0] as QuoteMark) : undefined);
const withOpening = (key: string, value: string, raw: string): KeyValuePair => {
  const opening = openingOf(raw);
  return opening ? { key, value, opening } : { key, value };
};

// ─── Plain ';' split (Azure Storage) ─────────────────────────────────────────────────────────────────────────────────

export type KeyValueSyntax = { separator: 'semicolon' };
export const PLAIN_SEMICOLON_SYNTAX: KeyValueSyntax = { separator: 'semicolon' };

export type KeyValueScan = {
  /** One entry per non-empty segment, in order: its pair, or null when it is not `key=value` (no `=`, or no key). */
  segments: Array<KeyValuePair | null>;
};

export function scanKeyValues(input: string, _syntax: KeyValueSyntax): KeyValueScan {
  const segments: KeyValueScan['segments'] = [];
  for (const raw of input.split(';')) {
    const segment = raw.trim();
    if (!segment) continue;
    const eq = segment.indexOf('=');
    const key = eq > 0 ? segment.slice(0, eq).trim() : '';
    const value = segment.slice(eq + 1).trim();
    segments.push(key ? withOpening(key, value, value) : null);
  }
  return { segments };
}

// ─── .NET DbConnectionOptions (SqlClient / ODBC rules) ───────────────────────────────────────────────────────────────

export type DotNetRules = 'sqlclient' | 'odbc';

const isWhite = (ch: string | undefined): boolean => ch !== undefined && /\s/u.test(ch);
// eslint-disable-next-line no-control-regex
const isControl = (ch: string): boolean => /[\u0000-\u001f\u007f-\u009f]/u.test(ch) && !isWhite(ch);

/**
 * Where a scanner rejected the text: `quote` — a quoted / braced value never closes (the author wrote that format's
 * quoting); `syntax` — anything else (a key without `=`, text after a closing mark, a control character …).
 */
export type ScanFailure = { failure: 'quote' | 'syntax' };
export type Scan = KeyValuePair[] | ScanFailure;

export const scanFailed = (scan: Scan): scan is ScanFailure => !Array.isArray(scan);

/** The pairs as .NET's connection-string parser reads them, or null where it throws. Keys keep their case, trimmed. */
export function scanDotNetConnectionString(input: string, rules: DotNetRules): KeyValuePair[] | null {
  const scan = scanDotNet(input, rules);
  return scanFailed(scan) ? null : scan;
}

/** `scanDotNetConnectionString`, saying why it rejected the text. */
export function scanDotNet(input: string, rules: DotNetRules): Scan {
  const syntax: ScanFailure = { failure: 'syntax' };
  const quote: ScanFailure = { failure: 'quote' };
  const odbc = rules === 'odbc';
  const pairs: KeyValuePair[] = [];
  const n = input.length;
  let i = 0;
  while (i < n) {
    const ch = input[i];
    if (ch === ';' || isWhite(ch)) {
      i += 1;
      continue;
    }
    if (isControl(ch)) return syntax;
    // Key, up to a single '=' ('==' is a literal '=' with SqlClient rules).
    let key = '';
    for (;;) {
      if (i >= n) return syntax;
      const c = input[i];
      if (c === '=') {
        if (!odbc && input[i + 1] === '=') {
          key += '=';
          i += 2;
          continue;
        }
        i += 1;
        break;
      }
      if (isControl(c)) return syntax;
      key += c;
      i += 1;
    }
    key = key.trimEnd();
    if (!key) return syntax;
    while (i < n && isWhite(input[i])) i += 1;
    const open = input[i];
    const close = odbc ? (open === '{' ? '}' : undefined) : open === '"' || open === "'" ? open : undefined;
    let value = '';
    let wrapped = false;
    if (close) {
      wrapped = true;
      let closed = false;
      for (i += 1; i < n; i += 1) {
        if (input[i] !== close) {
          value += input[i];
        } else if (input[i + 1] === close) {
          value += close;
          i += 1;
        } else {
          closed = true;
          i += 1;
          break;
        }
      }
      if (!closed) return quote;
      while (i < n && isWhite(input[i])) i += 1;
      if (i < n && input[i] !== ';') return syntax;
    } else {
      while (i < n && input[i] !== ';') {
        if (isControl(input[i])) return syntax;
        value += input[i];
        i += 1;
      }
      value = value.trim();
      if (!odbc && /["']$/u.test(value)) return syntax;
    }
    pairs.push(wrapped ? (odbc ? { key, value, braced: true } : { key, value }) : withOpening(key, value, value));
    i += 1;
  }
  return pairs;
}

// ─── libpq conninfo_parse ────────────────────────────────────────────────────────────────────────────────────────────

/** The pairs as libpq's `conninfo_parse` reads them, or null where it rejects the text (keywords are not checked here). */
export function scanLibpqConninfo(input: string): KeyValuePair[] | null {
  const scan = scanLibpq(input);
  return scanFailed(scan) ? null : scan;
}

/** `scanLibpqConninfo`, saying why it rejected the text. */
export function scanLibpq(input: string): Scan {
  const pairs: KeyValuePair[] = [];
  const n = input.length;
  let i = 0;
  while (i < n) {
    if (isWhite(input[i])) {
      i += 1;
      continue;
    }
    let key = '';
    while (i < n && input[i] !== '=' && !isWhite(input[i])) {
      key += input[i];
      i += 1;
    }
    while (i < n && isWhite(input[i])) i += 1;
    if (input[i] !== '=') return { failure: 'syntax' };
    i += 1;
    while (i < n && isWhite(input[i])) i += 1;
    let value = '';
    const quoted = input[i] === "'";
    // The value's text as written — an escaped `\"` is written quoting, not an opening quote.
    const written = input[i] ?? '';
    if (!quoted) {
      while (i < n && !isWhite(input[i])) {
        if (input[i] === '\\') {
          if (i + 1 < n) value += input[i + 1];
          i += 2;
        } else {
          value += input[i];
          i += 1;
        }
      }
    } else {
      let closed = false;
      i += 1;
      while (i < n) {
        if (input[i] === '\\') {
          if (i + 1 < n) value += input[i + 1];
          i += 2;
        } else if (input[i] === "'") {
          closed = true;
          i += 1;
          break;
        } else {
          value += input[i];
          i += 1;
        }
      }
      if (!closed) return { failure: 'quote' };
    }
    pairs.push(quoted ? { key, value } : withOpening(key, value, written));
  }
  return pairs;
}
